import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../hooks/AuthProvider';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../Firebase Functions/firebase';

// Styled Components
const Container = styled.div`
  padding: 2rem;
  background: linear-gradient(to right, #e0f7fa, #f1f8e9);
  min-height: 100vh;
`;

const StyledTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 2rem;
  text-align: center;
  text-transform: uppercase;
`;

const WidgetSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 2rem;
`;

const WidgetCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h3`
  color: #1b5e20;
  margin-bottom: 1rem;
`;

const StatusInfo = styled.div`
  display: flex;
  justify-content: space-between;
  color: #2c3e50;
  margin-bottom: 0.5rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
`;

const StyledButton = styled.button`
  background-color: #27ae60;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  width: 250px;
  text-align: center;
  transition: background-color 0.3s, transform 0.2s;

  &:hover {
    background-color: #219653;
    transform: translateY(-2px);
  }
`;

const TaskContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TaskCard = styled.div`
  background: ${({ completed }) => (completed ? '#d4edda' : '#fff3cd')};
  border: 1px solid ${({ completed }) => (completed ? '#c3e6cb' : '#ffeeba')};
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: box-shadow 0.2s;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const Modal = styled.div`
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.7);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 1.5rem;
  width: 90%;
  max-width: 500px;
  border-radius: 12px;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px; right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 400px;
  border-radius: 8px;
  border: none;
`;

// SupervisorDashboard Component
const SupervisorDashboard = () => {
  const { t } = useTranslation();
  const { currentUser: user, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchTasks = async () => {
      try {
        const q1 = query(collection(db, 'tasks'), where('supervisorEmail', '==', user.email));
        const q2 = query(collection(db, 'tasks'), where('assignedUserEmail', '==', user.email));

        const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
        const allDocs = [...snap1.docs, ...snap2.docs];
        const tasks = allDocs.map(doc => ({ id: doc.id, ...doc.data() }));

        setAssignedTasks(tasks);
      } catch (err) {
        console.error('Error loading tasks:', err);
        setError('Failed to load tasks.');
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [user]);

  const toggleVideoModal = () => setVideoModalOpen(!isVideoModalOpen);

  const completedTasks = assignedTasks.filter(task =>
    task.tasks?.every(sub => sub.completed)
  ).length;

  const totalTasks = assignedTasks.length;

  if (authLoading) return <p>Loading authentication...</p>;

  return (
    <Container>
      <StyledTitle>{t('Supervisor Dashboard')}</StyledTitle>

      {/* Activity Widgets */}
      <WidgetSection>
        <WidgetCard>
          <SectionTitle>📋 Task Summary</SectionTitle>
          <StatusInfo>
            <span>Total Punch Lists:</span>
            <span>{totalTasks}</span>
          </StatusInfo>
          <StatusInfo>
            <span>Completed Punch Lists:</span>
            <span>{completedTasks}</span>
          </StatusInfo>
        </WidgetCard>
      </WidgetSection>

      {/* Action Buttons */}
      <ButtonContainer>
        <StyledButton onClick={() => navigate('/job-form')}>{t('New Service Update')}</StyledButton>
        <StyledButton onClick={() => navigate('/saved-reports')}>{t('Saved Reports')}</StyledButton>
        <StyledButton onClick={() => navigate('/supervisor-job-list')}>{t('Services Needing Approval')}</StyledButton>
        <StyledButton onClick={() => navigate('/supervisor-trip-inspection')}>{t('Trip Inspection')}</StyledButton>
        <StyledButton onClick={() => navigate('/inspection-history')}>{t('Inspection History')}</StyledButton>
        <StyledButton onClick={() => navigate('/saved-incident-reports')}>{t('Incident Report Dashboard')}</StyledButton>
        <StyledButton onClick={toggleVideoModal}>{t('Watch Tutorial Video')}</StyledButton>
        <StyledButton onClick={() => window.open('https://employee-safe-space.vercel.app/', '_blank')}>
         {t('Leave Anonymous Feedback')}
        </StyledButton>

      </ButtonContainer>

      {/* Punch List */}
      <SectionTitle>My Punch List</SectionTitle>
      {loading && <p>Loading tasks...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}

      <TaskContainer>
        {assignedTasks.map(task => {
          const total = task.tasks?.length || 0;
          const completed = task.tasks?.filter(sub => sub.completed).length || 0;
          const isCompleted = total > 0 && completed === total;

          return (
            <TaskCard key={task.id} completed={isCompleted} onClick={() => navigate(`/task/${task.id}`)}>
              <p><strong>Property:</strong> {task.propertyName}</p>
              <p><strong>Due:</strong> {task.completionWeek || 'N/A'}</p>
              <p>{completed}/{total} Subtasks Completed</p>
            </TaskCard>
          );
        })}
      </TaskContainer>

      {/* Tutorial Video Modal */}
      <Modal isOpen={isVideoModalOpen} onClick={toggleVideoModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <CloseButton onClick={toggleVideoModal}>✖</CloseButton>
          <Iframe
            src="https://www.youtube.com/embed/LJoA59hKZbI"
            title="Tutorial Video"
          />
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default SupervisorDashboard;
